import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import { getCurrentUserId } from "@/utils"
import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  NativeSelectField,
  NativeSelectRoot,
  Stack,
  Text,
  VisuallyHidden,
} from "@chakra-ui/react"
import { useState } from "react"
import { FiClock } from "react-icons/fi"
import {
  PER_QUESTION_PRESETS_SECONDS,
  type StudentTimerMode,
  WHOLE_TEST_PRESETS_MINUTES,
  formatClock,
  formatDurationLabel,
} from "./studentTimer"
import { useStudentTimer } from "./useStudentTimer"

interface StudentTimerControlProps {
  attemptId: string
  questionId: string | undefined
  questionStartedAt: number
  instructorTimeLimited: boolean
  isSubmitted: boolean
  /** An answer save is in flight; the per-question clock pauses. */
  isSavingAnswer: boolean
}

const PHASE_PALETTE = {
  running: "gray",
  // Yellow, never red: overtime is gentle feedback, not an error.
  warning: "yellow",
  overtime: "yellow",
} as const

/**
 * Opt-in personal pacing timer for students. Advisory only: it never submits,
 * skips, or touches the instructor time limit.
 */
export function StudentTimerControl({
  attemptId,
  questionId,
  questionStartedAt,
  instructorTimeLimited,
  isSubmitted,
  isSavingAnswer,
}: StudentTimerControlProps) {
  const [userId] = useState(() => getCurrentUserId() ?? "anonymous")
  const { preference, setPreference, effectiveMode, status, announcement } =
    useStudentTimer({
      userId,
      attemptId,
      questionId,
      questionStartedAt,
      instructorTimeLimited,
      isSubmitted,
      paused: isSavingAnswer,
    })
  const clockLabel = effectiveMode === "whole_test" ? "My time" : "My pace"
  const clockText = status
    ? status.phase === "overtime"
      ? `${clockLabel} +${formatClock(status.overtimeSeconds)} over`
      : `${clockLabel} ${formatClock(status.remainingSeconds)}`
    : null

  return (
    <Box mt={2}>
      <HStack justify="flex-end" gap={2} flexWrap="wrap">
        {status && (
          <Badge
            colorPalette={PHASE_PALETTE[status.phase]}
            variant={status.phase === "overtime" ? "solid" : "subtle"}
            size="lg"
            fontVariantNumeric="tabular-nums"
          >
            {clockText}
          </Badge>
        )}
        <DialogRoot lazyMount size="sm">
          <DialogTrigger asChild>
            <Button
              colorPalette="teal"
              fontSize="xs"
              fontWeight="semibold"
              h="auto"
              minH="unset"
              px={2}
              py={1}
              size="xs"
              variant="outline"
            >
              <Icon as={FiClock} aria-hidden />
              My timer
            </Button>
          </DialogTrigger>
          <DialogContent
            maxW="sm"
            // The quiz page listens for Enter/letter shortcuts on window; keep
            // keys used inside this modal from advancing or answering.
            onKeyDown={(event) => event.stopPropagation()}
          >
            <DialogHeader>
              <DialogTitle>My pace timer</DialogTitle>
            </DialogHeader>
            <DialogCloseTrigger />
            <DialogBody pb={5}>
              <Stack gap={3}>
                <Text fontSize="sm" color="fg.muted">
                  Optional practice aid. It never submits or skips
                  {instructorTimeLimited
                    ? "; your instructor's time limit still applies."
                    : "."}
                </Text>
                <Field label="Timer mode">
                  <NativeSelectRoot size="sm">
                    <NativeSelectField
                      value={effectiveMode}
                      onChange={(event) =>
                        setPreference({
                          ...preference,
                          mode: event.currentTarget.value as StudentTimerMode,
                        })
                      }
                    >
                      <option value="off">Off</option>
                      <option value="per_question">Per question</option>
                      {!instructorTimeLimited && (
                        <option value="whole_test">Whole test</option>
                      )}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
                {effectiveMode === "per_question" && (
                  <Field label="Time per question">
                    <NativeSelectRoot size="sm">
                      <NativeSelectField
                        value={String(preference.perQuestionSeconds)}
                        onChange={(event) =>
                          setPreference({
                            ...preference,
                            perQuestionSeconds: Number(
                              event.currentTarget.value,
                            ),
                          })
                        }
                      >
                        {PER_QUESTION_PRESETS_SECONDS.map((seconds) => (
                          <option key={seconds} value={seconds}>
                            {formatDurationLabel(seconds)}
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                  </Field>
                )}
                {effectiveMode === "whole_test" && (
                  <Field label="Time for the whole test">
                    <NativeSelectRoot size="sm">
                      <NativeSelectField
                        value={String(preference.wholeTestMinutes)}
                        onChange={(event) =>
                          setPreference({
                            ...preference,
                            wholeTestMinutes: Number(event.currentTarget.value),
                          })
                        }
                      >
                        {WHOLE_TEST_PRESETS_MINUTES.map((minutes) => (
                          <option key={minutes} value={minutes}>
                            {formatDurationLabel(minutes * 60)}
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                  </Field>
                )}
                {effectiveMode !== "off" && (
                  <Checkbox
                    size="sm"
                    checked={preference.sound}
                    onCheckedChange={({ checked }) =>
                      setPreference({ ...preference, sound: checked === true })
                    }
                  >
                    Play a sound when time's up
                  </Checkbox>
                )}
              </Stack>
            </DialogBody>
          </DialogContent>
        </DialogRoot>
      </HStack>

      {status?.phase === "overtime" && (
        <Box
          mt={2}
          px={3}
          py={2}
          borderRadius="md"
          bg="yellow.subtle"
          color="yellow.fg"
          fontSize="sm"
        >
          <Text>
            Time's up — keep going. Your overtime will show in your review.
          </Text>
        </Box>
      )}

      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </VisuallyHidden>
    </Box>
  )
}
